import { supabaseClient } from './supabase';
import {
  VmtReductionComponents,
  getDefaultVmtComponents,
  EnergyEfficiencyComponents,
  getDefaultEnergyEfficiencyComponents,
  distributeEnergyEfficiencyTarget,
  hasAnyEfficiencyComponent,
  normalizeEnergyEfficiencyComponents,
  ParkingPricingAssumptions,
  MicromobilityAssumptions,
} from '../types/policyImpact';
import { applyEfficiencyForYear, type EfficiencyLayerOptions } from './buildingsEfficiencyModel';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { getBuildingsEnergyData, calculateTotalElectricityConsumption, calculateTotalNaturalGasConsumption } from './buildingsEnergyDataService';
import { fetchCarbonIntensityData } from './carbonIntensityService';
import { fetchElectricityCarbonIntensityGPerKwhForGeofipsAndYear } from './carbonIntensitySeriesCore';
import {
  getBuildingsBauPathway,
  getPathwayTherms,
  getPathwayKwh,
  getPathwayRngShare,
  getPathwayStockElectrified,
  getPathwayStockElectrifiedResidential,
  getPathwayStockElectrifiedCommercial,
  KWH_PER_THERM,
  type BuildingsBauScenario,
} from './buildingsBauPathways';
import {
  getDefaultBuildingEndUseAssumptions,
  type BuildingEndUseAssumptionsInput,
} from './buildingEndUseAssumptions';

// Policy scenario configuration
export interface PolicyScenario {
  evAdoptionPercent: number; // 0-100
  vmtReductionPercent: number; // 0-100 (computed from components)
  vmtReductionComponents: VmtReductionComponents; // NEW: component breakdown
  /** Percent of homes in or near downtown (≤½ distance to downtown); default 70. These generate 29% fewer VMT. */
  downtownHomesPercent?: number; // 0-100, default 70
  parkingPricing: ParkingPricingAssumptions; // NEW: parking pricing policy
  micromobility?: MicromobilityAssumptions; // NEW: expand micromobility program
  /** Top-level energy efficiency target (0–40) when collapsed; computed total when expanded. Replaces legacy energyEfficiencyPercent. */
  energyEfficiencyTargetPercent: number; // 0-40 by 2045
  /** Policy levers; when set, total efficiency is computed from these. When collapsed, derived from target. */
  energyEfficiencyComponents?: EnergyEfficiencyComponents;
  /** @deprecated Use energyEfficiencyTargetPercent. Kept for cache key / display compatibility. */
  energyEfficiencyPercent?: number; // 0-40 by 2045
  heatPumpAdoptionPercent: number; // 0-100
  gridCleanElectricityYear: number; // year electricity reaches zero emissions (2025-2045), default 2045 (SB 100)
  solarCapacityMultiplier: number; // 1.0 = current, 2.0 = double, etc.
  hdvElectrificationPercent: number; // 0-100
  /**
   * Additional organics diversion beyond BAU by 2045 (percentage points, 0–50).
   * Simple first draft: scale waste-sector emissions proportionally to avoided organics-to-landfill.
   * IMPORTANT: BAU already embeds statewide organics policy; this lever is local incremental.
   */
  organicsDiversionPercent: number; // 0-50 (additional, by 2045)
  /**
   * Residential/commercial gas end-use shares (fractions 0–1, normalized per sector).
   * Electrification applies to space + water heating only.
   */
  buildingEndUseAssumptions?: BuildingEndUseAssumptionsInput | null;
  /**
   * When true (default), BAU uses Buildings scenario–dependent efficiency improvement on existing stock by 2045
   * (Conservative 0%, Current Policy 10%, Accelerated 20%) in the stock model.
   */
  applyBauExistingStockEfficiency?: boolean;
  /**
   * Optional override (0–40%) for cumulative BAU efficiency on existing stock by 2045, linear from 2023.
   * When omitted, the Buildings BAU pathway default is used (Conservative 0%, Current policy 10%, Accelerated 20%).
   */
  bauExistingStockEfficiencyPercent2045?: number;
}

// Baseline GHG data
export interface BaselineGHGData {
  lightDutyGHG: number; // metric tons CO2e
  heavyDutyGHG: number; // metric tons CO2e
  naturalGasGHG: number; // metric tons CO2e
  electricityGHG: number; // metric tons CO2e
  wasteGHG: number; // metric tons CO2e
  /** County agricultural GHG (ag_emissions); 0 for cities */
  agricultureGHG: number;
  electricityKwh: number; // kWh/year (from Buildings page)
  /** Location-specific g CO2e/kWh; year matches `baselineInventoryYear` (inventory / Buildings path). */
  electricityCarbonIntensityGPerKwh: number;
  /** Calendar year for inventory baseline (VMT, buildings energy, electricity CI above). */
  baselineInventoryYear: number;
  totalGHG: number; // metric tons CO2e
  vmt: number; // vehicle miles traveled
  naturalGasTherms: number;
  solarCapacityKw: number;
  currentEVPercent: number; // 2045 BAU EV% (used as slider default/minimum)
  currentEVPercent2023?: number; // 2023 EV% (for display)
  population: number;
  /** BAU 2045 building therms (from stock-based pathway). When set, summary uses this as building baseline. */
  bauTherms2045?: number;
  /** BAU 2045 building kWh (from pathway). When set, summary uses this as building baseline. */
  bauKwh2045?: number;
  /** RNG share (0–1) in 2045 for building NG emissions. When set, NG emissions = therms * (1 - rngShare2045) * 5.29/1000. */
  rngShare2045?: number;
  /**
   * BAU aggregate share (0–1) of **electrifiable** building gas converted by 2045 (default end-use split).
   * For display / legacy single-line summaries.
   */
  bauElectrifiedShare2045?: number;
  /** Residential sector: turnover stock share (0–1) in 2045 — 1 = all electrifiable res. gas converted under BAU. */
  bauElectrifiedShareResidential2045?: number;
  /** Commercial sector: same as residential, for commercial electrifiable gas. */
  bauElectrifiedShareCommercial2045?: number;
}

// Policy impact results
export interface PolicyImpactResults {
  baseline: BaselineGHGData;
  projected: {
    lightDutyGHG: number;
    heavyDutyGHG: number;
    naturalGasGHG: number;
    electricityGHG: number; // projected electricity emissions (efficiency applied; electrification impact handled later)
    wasteGHG: number;
    agricultureGHG: number;
    totalGHG: number;
  };
  reductions: {
    lightDutyReduction: number;
    heavyDutyReduction: number;
    naturalGasReduction: number;
    electricityReduction: number;
    wasteReduction: number;
    totalReduction: number;
    percentReduction: number;
  };
  contributionsByPolicy: {
    evAdoption: number;
    vmtReduction: number;
    vmtReductionByComponent?: {
      transitOrientedDevelopment: number;
      activeTransportation: number;
      transitImprovements: number;
    };
    downtownHomes?: number; // VMT reduction from % of homes in/near downtown (29% fewer VMT per such home)
    parkingPricing: number;
    micromobility?: number;
    energyEfficiency: number;
    heatPumpAdoption: number;
    solarCapacity: number;
    hdvElectrification: number;
    organicsDiversion: number;
  };
}

/**
 * Fetch EMFAC data for Policy Tool (same approach as GHG Inventory)
 * @param location - Location name
 * @param emfacVersion - '2021' or '2025' to select EMFAC version
 */
async function fetchEMFACDataForPolicy(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<any[]> {
  // Map city to county for EMFAC queries
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const emfacLocation = `${baseCountyName} County`;
  
  // Select table based on EMFAC version
  const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';
  
  // Try different location name variations (same as GHG Inventory)
  const locationVariations = [
    emfacLocation,
    baseCountyName,
    `${baseCountyName} County`
  ];
  
  for (const loc of locationVariations) {
    console.log(`Policy Impact: Trying EMFAC${emfacVersion} county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('year, ghg_per_mile')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    
    if (error) {
      console.log(`Policy Impact: EMFAC${emfacVersion} county filtered query error for`, loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      console.log(`Policy Impact: EMFAC${emfacVersion} county filtered data found for`, loc, ':', data);
      return data;
    }
  }
  
  console.log(`Policy Impact: No EMFAC${emfacVersion} county filtered data found for any location variation:`, locationVariations);
  
  // Fallback: Try California data (same as GHG Inventory)
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    console.log(`Policy Impact: Trying California EMFAC${emfacVersion} county filtered: ${californiaName}`);
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from(tableName)
      .select('year, ghg_per_mile')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .order('year', { ascending: true });
    
    if (californiaError) {
      console.log(`Policy Impact: California EMFAC${emfacVersion} county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaData && californiaData.length > 0) {
      console.log(`Policy Impact: Using California EMFAC${emfacVersion} county filtered data for ${californiaName}:`, californiaData);
      return californiaData;
    }
  }
  
  console.log(`Policy Impact: No EMFAC${emfacVersion} data available`);
  return [];
}

// Constants for calculations
const CONSTANTS = {
  // Grid carbon intensity (kg CO2/kWh)
  /** kg CO2/kWh — fallback when location electricity CI missing */
  BASELINE_GRID_INTENSITY: 0.2,

  // EV constants
  EV_KWH_PER_MILE: 0.3, // Average EV efficiency
  ICE_IMPROVEMENT_FACTOR: 0.95, // Future ICE efficiency improvement

  // Heat pump constants
  HEAT_PUMP_COP: 3.5, // Coefficient of performance
  KWH_PER_THERM: 29.3, // Energy content conversion
  THERMS_PER_MT_CO2: 0.005291, // Natural gas emissions factor

  // Heavy duty vehicle constants
  HDV_ELECTRIFICATION_EFFICIENCY: 0.4, // EVs use 40% of energy of diesel

  // Solar constants
  SOLAR_DISPLACEMENT_FACTOR: 0.8, // Accounts for curtailment, storage, etc.
  ANNUAL_SOLAR_HOURS: 2000, // Average annual solar production hours
};

/**
 * Memo for policy-tool baseline: inventory + buildings + EMFAC lookups.
 * Scenario sliders (VMT %, EV %, clean electricity year, etc.) do not change this; only location / EMFAC / buildings BAU path do.
 * Cleared alongside policy time-series baseline when VMT, buildings, or location context changes.
 */
const baselineGHGDataCache = new Map<string, BaselineGHGData>();
const baselineGHGDataInflight = new Map<string, Promise<BaselineGHGData>>();

function baselineGHGDataCacheKey(
  location: string,
  emfacVersion: '2021' | '2025',
  buildingsBauScenario?: BuildingsBauScenario
): string {
  return `${location}\0${emfacVersion}\0${buildingsBauScenario ?? ''}`;
}

/** Drop cached policy baseline for one place or all (e.g. when VMT or buildings data changes). */
export function clearBaselineGHGDataCache(location?: string): void {
  if (location) {
    const prefix = `${location}\0`;
    Array.from(baselineGHGDataCache.keys()).forEach((k) => {
      if (k.startsWith(prefix)) baselineGHGDataCache.delete(k);
    });
    Array.from(baselineGHGDataInflight.keys()).forEach((k) => {
      if (k.startsWith(prefix)) baselineGHGDataInflight.delete(k);
    });
  } else {
    baselineGHGDataCache.clear();
    baselineGHGDataInflight.clear();
  }
}

/**
 * Fetch baseline GHG data for a location using the same data source as GHG inventory.
 * When buildingsBauScenario is provided, also computes BAU 2045 building values (therms, kWh, RNG share) from stock-based pathway.
 */
export async function fetchBaselineGHGData(
  location: string,
  emfacVersion: '2021' | '2025' = '2025',
  buildingsBauScenario?: BuildingsBauScenario
): Promise<BaselineGHGData> {
  const key = baselineGHGDataCacheKey(location, emfacVersion, buildingsBauScenario);
  const cached = baselineGHGDataCache.get(key);
  if (cached) return cached;

  const inflight = baselineGHGDataInflight.get(key);
  if (inflight) return inflight;

  const promise = fetchBaselineGHGDataUncached(location, emfacVersion, buildingsBauScenario)
    .then((data) => {
      baselineGHGDataCache.set(key, data);
      baselineGHGDataInflight.delete(key);
      return data;
    })
    .catch((err) => {
      baselineGHGDataInflight.delete(key);
      throw err;
    });

  baselineGHGDataInflight.set(key, promise);
  return promise;
}

async function fetchBaselineGHGDataUncached(
  location: string,
  emfacVersion: '2021' | '2025' = '2025',
  buildingsBauScenario?: BuildingsBauScenario
): Promise<BaselineGHGData> {
  try {
    // Use the same data source as the GHG inventory chart
    const inventoryData = await fetchGHGInventoryData(location);
    
    // Extract data from the inventory result - this already includes all the complex calculations
    const lightDutyGHG = inventoryData.lightDutyEmissions || 0;
    const heavyDutyGHG = inventoryData.heavyDutyEmissions || 0;
    const naturalGasGHG = inventoryData.naturalGasEmissions || 0;
    const totalGHG = inventoryData.totalEmissions || 0;
    
    // Get the latest year from inventory data
    const latestYear = inventoryData.latestYear || 2023;
    
    // Get VMT data directly from the same source as GHG Inventory
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
    } catch (error) {
      console.warn('Could not get geofips for VMT data:', error);
    }
    
    // Import and use the same VMT data service as GHG Inventory
    const { getVMTDataForGHG } = await import('./vmtDataService');
    const vmtData = await getVMTDataForGHG(location, geofips || undefined, emfacVersion);
    
    // Get the latest VMT value (2023)
    const latestVmtData = vmtData.find(d => d.year === latestYear);
    const vmt = latestVmtData?.vmt || 0;
    
    console.log('Policy Tool: VMT data for', location, ':', vmtData.length, 'records');
    console.log('Policy Tool: Latest VMT (', latestYear, '):', vmt);
    
    let electricityKwh = 0;
    let naturalGasTherms = 0;
    let bauTherms2045: number | undefined;
    let bauKwh2045: number | undefined;
    let rngShare2045: number | undefined;
    let bauElectrifiedShare2045: number | undefined;
    let bauElectrifiedShareResidential2045: number | undefined;
    let bauElectrifiedShareCommercial2045: number | undefined;
    try {
      const buildings = await getBuildingsEnergyData(location);
      electricityKwh = calculateTotalElectricityConsumption(buildings, latestYear);
      naturalGasTherms = calculateTotalNaturalGasConsumption(buildings, latestYear);
      if (buildingsBauScenario) {
        const pathway = getBuildingsBauPathway(buildings, buildingsBauScenario);
        bauTherms2045 = getPathwayTherms(pathway, 2045);
        bauKwh2045 = getPathwayKwh(pathway, 2045);
        rngShare2045 = getPathwayRngShare(pathway, 2045);
        bauElectrifiedShare2045 = getPathwayStockElectrified(pathway, 2045);
        bauElectrifiedShareResidential2045 = getPathwayStockElectrifiedResidential(pathway, 2045);
        bauElectrifiedShareCommercial2045 = getPathwayStockElectrifiedCommercial(pathway, 2045);
      }
    } catch (e) {
      console.warn('Could not fetch Buildings energy consumption for baseline; defaulting to 0s:', e);
      electricityKwh = 0;
      naturalGasTherms = 0;
    }

    // Prefer `electricity_carbon_intensity` for (geofips, latestYear); else Buildings chart series.
    let electricityCarbonIntensityGPerKwh = 300; // fallback gCO2e/kWh
    try {
      await supabaseLocationService.initialize();
      const resolvedGeofips = await supabaseLocationService.getGeofips(location);
      if (resolvedGeofips) {
        const gf = Number(resolvedGeofips);
        let fromTable = false;
        if (Number.isFinite(gf)) {
          const row = await fetchElectricityCarbonIntensityGPerKwhForGeofipsAndYear(supabaseClient, gf, latestYear);
          if (row && row.value > 0) {
            electricityCarbonIntensityGPerKwh = row.value;
            fromTable = true;
          }
        }
        if (!fromTable) {
          const ci = await fetchCarbonIntensityData(String(resolvedGeofips));
          const years = (ci.chartData?.labels || []) as any[];
          const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
          const idx = years.findIndex((y: any) => Number(y) === Number(latestYear) || String(y) === String(latestYear));
          if (idx >= 0) {
            const v = Number(data[idx]);
            if (Number.isFinite(v) && v > 0) electricityCarbonIntensityGPerKwh = v;
          }
        }
      }
    } catch (e) {
      console.warn('Could not fetch carbon intensity for baseline; using fallback:', e);
    }

    // Baseline electricity emissions from kWh * (g/kWh)
    const electricityGHG = (electricityKwh * electricityCarbonIntensityGPerKwh) / 1_000_000;
    
    // Extract waste emissions from inventory chart data
    let wasteGHG = 0;
    try {
      const chartData = inventoryData.chartData;
      if (chartData?.datasets) {
        const wasteDataset = chartData.datasets.find((d: any) => d.label === 'Waste' || d.label?.includes('Waste'));
        if (wasteDataset?.data) {
          const labels = chartData.labels || [];
          const yearIndex = labels.findIndex((y: any) => Number(y) === Number(latestYear) || String(y) === String(latestYear));
          if (yearIndex >= 0 && yearIndex < wasteDataset.data.length) {
            const v = Number(wasteDataset.data[yearIndex]);
            if (Number.isFinite(v) && v > 0) wasteGHG = v;
          }
        }
      }
    } catch (e) {
      console.warn('Could not extract waste emissions from inventory chart data; defaulting to 0:', e);
    }

    let agricultureGHG = 0;
    try {
      const chartData = inventoryData.chartData;
      if (chartData?.datasets) {
        const agDataset = chartData.datasets.find((d: any) => d.label === 'Agriculture');
        if (agDataset?.data) {
          const labels = chartData.labels || [];
          const yearIndex = labels.findIndex((y: any) => Number(y) === Number(latestYear) || String(y) === String(latestYear));
          if (yearIndex >= 0 && yearIndex < agDataset.data.length) {
            const v = Number(agDataset.data[yearIndex]);
            if (Number.isFinite(v) && v > 0) agricultureGHG = v;
          }
        }
      }
    } catch (e) {
      console.warn('Could not extract agriculture emissions from inventory chart; defaulting to 0:', e);
    }
    
    // Fetch solar capacity using county or geofips to avoid city-name 400s
    let solarCapacityKw = 0;
    try {
      let solarQuery = supabaseClient
        .from('all_pv_size')
        .select('kw_total')
        .eq('year', latestYear);

      if (geofips) {
        solarQuery = solarQuery.eq('geoid', geofips);
      } else {
        const countyName = supabaseLocationService.getCountyForCity(location) || location;
        // Prefer county aggregation; fall back to city rows within county
        const { data: countyRows } = await solarQuery
          .ilike('location_name', `%${(countyName || '').replace(' County', '').replace(' Co.', '')}%`)
          .eq('geography', 'County');
        if (countyRows && countyRows.length > 0) {
          solarCapacityKw = countyRows.reduce((sum, row: any) => sum + (row.kw_total || 0), 0);
        } else {
          const { data: cityRows } = await supabaseClient
            .from('all_pv_size')
            .select('kw_total, county, geography')
            .eq('year', latestYear)
            .ilike('county', `%${(countyName || '').replace(' County', '').replace(' Co.', '')}%`)
            .eq('geography', 'City');
          solarCapacityKw = cityRows?.reduce((sum, row: any) => sum + (row.kw_total || 0), 0) || 0;
        }
      }
    } catch (e) {
      console.warn('Solar capacity lookup failed, defaulting to 0:', e);
      solarCapacityKw = 0;
    }
    
    // Fetch EV adoption data from emfac2021_county (2021) or emfac2025_county (2025) for both 2023 (current) and 2045 (BAU) at county level
    const evTableName = emfacVersion === '2025' ? 'emfac2025_county' : 'emfac2021_county';
    let totalEVs2023 = 0;
    let totalVehicles2023 = 0;
    let totalEVs2045 = 0;
    let totalVehicles2045 = 0;
    
    try {
      await supabaseLocationService.initialize();
      
      // Get county name for the location (EMFAC data is at county level)
      // If location is already a county, use it directly; otherwise get county from city
      const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
      const baseCountyName = resolvedCounty.replace(/\s+County$/i, '').trim();
      const countyName = `${baseCountyName} County`;
      
      // Try different county name variations
      const locationVariations = [
        countyName,
        baseCountyName,
        resolvedCounty,
        location // Also try the original location name in case it's already a county
      ];
      
      console.log(`🔍 Looking up EV adoption for location: "${location}" using ${evTableName} (EMFAC${emfacVersion})`);
      console.log(`   Resolved county: "${resolvedCounty}"`);
      console.log(`   Trying variations:`, locationVariations);
      
      for (const loc of locationVariations) {
        // Get 2023 EV data (current year)
        // Note: Only count "Electricity" as EV fuel type (exclude PHEV and FCEV)
        const { data: evData2023, error: evError2023 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('fuel', 'Electricity')
          .eq('year', 2023);

        const { data: allLdvData2023, error: allLdvError2023 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('year', 2023);

        // Get 2045 EV data (BAU projection - this is the default for slider)
        // Note: Only count "Electricity" as EV fuel type (exclude PHEV and FCEV)
        const { data: evData2045, error: evError2045 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('fuel', 'Electricity')
          .eq('year', 2045);

        const { data: allLdvData2045, error: allLdvError2045 } = await supabaseClient
          .from(evTableName)
          .select('year, fuel, total_fleet, class')
          .eq('location_name', loc)
          .eq('geography_type', 'County')
          .eq('class', 'LDV')
          .eq('year', 2045);

        if (!evError2023 && evData2023 && evData2023.length > 0) {
          totalEVs2023 = evData2023.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }

        if (!allLdvError2023 && allLdvData2023 && allLdvData2023.length > 0) {
          totalVehicles2023 = allLdvData2023.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }

        if (!evError2045 && evData2045 && evData2045.length > 0) {
          totalEVs2045 = evData2045.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }

        if (!allLdvError2045 && allLdvData2045 && allLdvData2045.length > 0) {
          totalVehicles2045 = allLdvData2045.reduce((sum: number, row: any) => sum + (Number(row.total_fleet) || 0), 0);
        }
        
        // If we found 2045 data, break out of the loop (2045 is what we need for the slider default)
        if (totalVehicles2045 > 0) {
          console.log(`✅ Found EV adoption data for "${loc}":`);
          console.log(`   2023: ${totalEVs2023.toLocaleString()} EVs / ${totalVehicles2023.toLocaleString()} total LDV vehicles`);
          console.log(`   2045: ${totalEVs2045.toLocaleString()} EVs / ${totalVehicles2045.toLocaleString()} total LDV vehicles`);
          break;
        } else if (evError2045 || allLdvError2045) {
          console.warn(`⚠️ Error querying "${loc}":`, evError2045 || allLdvError2045);
        }
      }
      
      if (totalVehicles2045 === 0) {
        console.warn(`⚠️ No EV adoption data found for any location variation. Tried:`, locationVariations);
        console.warn(`   This may indicate missing data in ${evTableName} for year 2045`);
      }
    } catch (e) {
      console.error(`❌ EV adoption lookup from ${evTableName} failed:`, e);
      console.warn('   Defaulting to 0% for EV adoption slider');
    }
    
    // Calculate EV percentages
    // 2023: Current EV percentage (for display)
    const currentEVPercent2023 = totalVehicles2023 > 0 ? Number(((totalEVs2023 / totalVehicles2023) * 100).toFixed(2)) : 0;
    
    // 2045: BAU EV percentage (this is the default/minimum for the slider)
    const currentEVPercent = totalVehicles2045 > 0 ? Number(((totalEVs2045 / totalVehicles2045) * 100).toFixed(2)) : 0;
    
    console.log(`📊 EV Adoption: 2023=${currentEVPercent2023.toFixed(2)}%, 2045 BAU=${currentEVPercent.toFixed(2)}% (slider default)`);
    
    // Fetch population
    const { data: popData } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', location)
      .eq('year', latestYear)
      .single();
    
    const population = popData?.population || 0;

    console.log(`Fetched baseline GHG data for ${location} (${latestYear}):`, {
      lightDutyGHG,
      heavyDutyGHG,
      naturalGasGHG,
      electricityGHG,
      wasteGHG,
      agricultureGHG,
      totalGHG,
      vmt,
      naturalGasTherms,
      solarCapacityKw,
      currentEVPercent,
      population
    });

    return {
      lightDutyGHG,
      heavyDutyGHG,
      naturalGasGHG,
      electricityGHG,
      wasteGHG,
      agricultureGHG,
      electricityKwh,
      electricityCarbonIntensityGPerKwh,
      baselineInventoryYear: latestYear,
      totalGHG,
      vmt,
      naturalGasTherms,
      solarCapacityKw,
      currentEVPercent,
      currentEVPercent2023,
      population,
      ...(bauTherms2045 !== undefined && { bauTherms2045 }),
      ...(bauKwh2045 !== undefined && { bauKwh2045 }),
      ...(rngShare2045 !== undefined && { rngShare2045 }),
      ...(bauElectrifiedShare2045 !== undefined && { bauElectrifiedShare2045 }),
      ...(bauElectrifiedShareResidential2045 !== undefined && { bauElectrifiedShareResidential2045 }),
      ...(bauElectrifiedShareCommercial2045 !== undefined && { bauElectrifiedShareCommercial2045 }),
    };
  } catch (error) {
    console.error('Error fetching baseline GHG data:', error);
    throw error;
  }
}

/**
 * Grid carbon intensity (kg CO2/kWh) after a simple solar-penetration adjustment.
 * `baselineGridIntensityKgPerKwh` is anchored to location inventory when provided (g/kWh from baseline).
 */
function calculateGridCarbonIntensity(
  solarCapacityKw: number,
  population: number,
  baselineGridIntensityKgPerKwh: number = CONSTANTS.BASELINE_GRID_INTENSITY
): number {
  // Estimate total electricity consumption (kWh/year)
  // California average: ~7,000 kWh per capita per year
  const totalElectricityConsumption = population * 7000;

  // Estimate solar generation (kWh/year)
  const solarGeneration = solarCapacityKw * CONSTANTS.ANNUAL_SOLAR_HOURS;

  // Calculate solar penetration
  const solarPenetration = totalElectricityConsumption > 0
    ? solarGeneration / totalElectricityConsumption
    : 0;

  const anchor =
    Number.isFinite(baselineGridIntensityKgPerKwh) && baselineGridIntensityKgPerKwh > 0
      ? baselineGridIntensityKgPerKwh
      : CONSTANTS.BASELINE_GRID_INTENSITY;

  return anchor * (1 - Math.min(solarPenetration * CONSTANTS.SOLAR_DISPLACEMENT_FACTOR, 0.9));
}

/**
 * Same grid-intensity model as EV / micromobility policy calculations (kg CO2e/kWh).
 */
export function gridCarbonIntensityKgPerKwhForPolicy(
  solarCapacityKw: number,
  population: number,
  electricityCarbonIntensityGPerKwh: number
): number {
  const baselineKg =
    electricityCarbonIntensityGPerKwh > 0
      ? electricityCarbonIntensityGPerKwh / 1000
      : CONSTANTS.BASELINE_GRID_INTENSITY;
  return calculateGridCarbonIntensity(solarCapacityKw, population, baselineKg);
}

/**
 * Calculate VMT reduction from parking pricing policy
 * Uses elasticity-based approach from Pierce & Shoup 2013
 */
function calculateParkingPricingVMTReduction(
  parkingAssumptions: ParkingPricingAssumptions,
  baselineVMT: number
): number {
  if (parkingAssumptions.priceIncreasePercent === 0) return 0;
  
  const SF_COMMUTE_BY_CAR_PERCENT = 30.29; // San Francisco baseline
  const BASE_ELASTICITY = -0.4; // Pierce and Shoup 2013
  
  // Calculate elasticity based on commute by car percentage
  let elasticity = 0;
  if (parkingAssumptions.commuteByCarPercent > 0 && parkingAssumptions.commuteByCarPercent < 100) {
    elasticity = BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / parkingAssumptions.commuteByCarPercent);
  }
  
  // Calculate percent VMT reduction based on elasticity and price change
  const vmtReductionPercent = Math.abs(elasticity) * (parkingAssumptions.priceIncreasePercent / 100);
  
  // Apply only to the percentage of VMT that can be affected
  const effectiveReductionPercent = vmtReductionPercent * (parkingAssumptions.vmtAffectedPercent / 100);
  
  return effectiveReductionPercent * 100; // Return as percentage
}

// Calculate micromobility GHG reduction (tCO2e/year)
async function calculateMicromobilityReduction(
  location: string,
  assumptions: MicromobilityAssumptions,
  emfacGhgPerMile: number,
  gridCarbonIntensity: number
): Promise<number> {
  try {
    // Fetch latest population (use 2025+ nearest) for location
    const { data: popRows } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', location)
      .order('year', { ascending: false })
      .limit(1);
    const population = popRows?.[0]?.population || 0;

    const scale = Math.max(0, assumptions.scalePercent || 0) / 100; // 0-2.0

    // Trips per resident
    const totalTrips = (assumptions.tripsPerResident || 0) * population * scale;
    const bikeTrips = totalTrips * ((assumptions.percentBike || 0) / 100);
    const scooterTrips = totalTrips * ((assumptions.percentScooter || 0) / 100);

    // Annual miles
    const bikeMiles = bikeTrips * (assumptions.milesPerBikeTrip || 0);
    const scooterMiles = scooterTrips * (assumptions.milesPerScooterTrip || 0);

    // Displaced car miles
    const displacedCarMiles =
      bikeMiles * ((assumptions.percentBikeTripsDisplaceCars || 0) / 100) +
      scooterMiles * ((assumptions.percentScooterTripsDisplaceCars || 0) / 100);

    // Avoided car emissions
    const avoidedCarTons = displacedCarMiles * emfacGhgPerMile; // tons CO2e

    // Electricity use and emissions
    const kwhBike = bikeMiles * (assumptions.kwhPerMileBike || 0);
    const kwhScooter = scooterMiles * (assumptions.kwhPerMileScooter || 0);
    // gridCarbonIntensity is kg CO2 per kWh in this file's constants; convert to tons
    const electricityTons = ((kwhBike + kwhScooter) * gridCarbonIntensity) / 1000;

    const netTons = Math.max(0, avoidedCarTons - electricityTons);
    return netTons;
  } catch (e) {
    console.warn('Micromobility reduction calculation failed:', e);
    return 0;
  }
}

// Detailed stats for Micromobility policy (for UI display)
export interface MicromobilityStats {
  population: number;
  scalePercent: number;
  tripsPerResident: number;
  totalProgramTrips: number;
  bikeTrips: number;
  scooterTrips: number;
  bikeMiles: number;
  scooterMiles: number;
  displacedCarMiles: number; // VMT reduced
  emfacGhgPerMile: number; // tons/mile used
  avoidedCarTons: number;
  electricityKwh: number;
  electricityTons: number;
  netTons: number;
}

export async function getMicromobilityStats(
  location: string,
  scenario: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025'
): Promise<MicromobilityStats> {
  const baseline = await fetchBaselineGHGData(location, emfacVersion);
  // Determine emission factor consistent with baseline
  const emfacData = await fetchEMFACDataForPolicy(location, emfacVersion);
  const validEmissionFactors = emfacData.filter((d) => d.ghg_per_mile > 0);
  const ef2023 = validEmissionFactors.find((d: any) => d.year === 2023)?.ghg_per_mile;
  const avgEmissionFactor = validEmissionFactors.length > 0
    ? validEmissionFactors.reduce((sum: number, d: any) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
    : 0.0008;
  const emfacGhgPerMile = ef2023 || (baseline.lightDutyGHG / Math.max(1, baseline.vmt)) || avgEmissionFactor;

  // Grid carbon intensity based on current solar scale (kg/kWh), location CI anchor
  const gridCarbonIntensity = gridCarbonIntensityKgPerKwhForPolicy(
    baseline.solarCapacityKw * (scenario.solarCapacityMultiplier || 1),
    baseline.population,
    baseline.electricityCarbonIntensityGPerKwh
  );

  const a = scenario.micromobility || {
    yearImplemented: 2025,
    tripsPerResident: 3,
    percentBike: 60,
    percentScooter: 40,
    milesPerBikeTrip: 1.61,
    milesPerScooterTrip: 1.2,
    percentBikeTripsDisplaceCars: 30,
    percentScooterTripsDisplaceCars: 20,
    kwhPerMileBike: 0.02,
    kwhPerMileScooter: 0.04,
    scalePercent: 0,
  };

  const scale = Math.max(0, a.scalePercent || 0) / 100; // 0-2.0
  const tripsPerResident = a.tripsPerResident || 0;
  const totalProgramTrips = baseline.population * tripsPerResident * scale;
  const bikeTrips = totalProgramTrips * ((a.percentBike || 0) / 100);
  const scooterTrips = totalProgramTrips * ((a.percentScooter || 0) / 100);

  const bikeMiles = bikeTrips * (a.milesPerBikeTrip || 0);
  const scooterMiles = scooterTrips * (a.milesPerScooterTrip || 0);

  const displacedCarMiles =
    bikeMiles * ((a.percentBikeTripsDisplaceCars || 0) / 100) +
    scooterMiles * ((a.percentScooterTripsDisplaceCars || 0) / 100);

  const avoidedCarTons = displacedCarMiles * emfacGhgPerMile;
  const electricityKwh = bikeMiles * (a.kwhPerMileBike || 0) + scooterMiles * (a.kwhPerMileScooter || 0);
  const electricityTons = (electricityKwh * gridCarbonIntensity) / 1000;
  const netTons = Math.max(0, avoidedCarTons - electricityTons);

  return {
    population: baseline.population,
    scalePercent: a.scalePercent || 0,
    tripsPerResident,
    totalProgramTrips: Math.round(totalProgramTrips),
    bikeTrips: Math.round(bikeTrips),
    scooterTrips: Math.round(scooterTrips),
    bikeMiles: Math.round(bikeMiles),
    scooterMiles: Math.round(scooterMiles),
    displacedCarMiles: Math.round(displacedCarMiles),
    emfacGhgPerMile,
    avoidedCarTons: Math.round(avoidedCarTons),
    electricityKwh: Math.round(electricityKwh),
    electricityTons: Math.round(electricityTons * 10) / 10,
    netTons: Math.round(netTons * 10) / 10,
  };
}

/** Step indicator while policy impacts are computed (summary card + chart prep). */
export interface PolicyLoadProgress {
  stepIndex: number;
  stepCount: number;
  label: string;
}

/**
 * Calculate policy impact on GHG emissions
 * @param location - Location name
 * @param scenario - Policy scenario
 * @param emfacVersion - '2021' or '2025' to select EMFAC version
 * @param options - Optional overrides; compactDevelopmentLdvReductionMT; buildingsBauScenario; buildingsRngPercentOverride (0–100); buildingsCopOverride (heat pump COP, default 3.5)
 */
export async function calculatePolicyImpact(
  location: string,
  scenario: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025',
  options?: {
    compactDevelopmentLdvReductionMT?: number;
    buildingsBauScenario?: BuildingsBauScenario;
    buildingsRngPercentOverride?: number;
    buildingsCopOverride?: number;
    efficiencyLayerOptions?: EfficiencyLayerOptions;
    /** Fired between major async phases so the UI can show step N of M. */
    onCalculationProgress?: (progress: PolicyLoadProgress) => void;
  }
): Promise<PolicyImpactResults> {
  const report = (stepIndex: number, label: string) => {
    options?.onCalculationProgress?.({ stepIndex, stepCount: 3, label });
  };

  report(1, 'Loading baseline emissions and energy use…');
  const baseline = await fetchBaselineGHGData(location, emfacVersion, options?.buildingsBauScenario);

  // Grid decarbonization lever is modeled as the year electricity reaches zero emissions (g/kWh -> 0).
  // In this 2045 summary, electricity is either clean (if year <= 2045) or not.
  // Since the slider range is 2025–2045, by definition electricity is clean by 2045.
  // (The lever mainly changes cumulative emissions before 2045, which is visible in the time series.)
  const gPerKwh2045 = 0;

  // Keep existing solar logic for EV/grid interactions (separate from the grid decarb lever for now).
  const newSolarCapacity = baseline.solarCapacityKw * scenario.solarCapacityMultiplier;
  const gridCarbonIntensity = gridCarbonIntensityKgPerKwhForPolicy(
    newSolarCapacity,
    baseline.population,
    baseline.electricityCarbonIntensityGPerKwh
  );
  const baselineGridIntensity = gridCarbonIntensityKgPerKwhForPolicy(
    baseline.solarCapacityKw,
    baseline.population,
    baseline.electricityCarbonIntensityGPerKwh
  );
  const gridDecarbonizationFactor = baselineGridIntensity > 0 ? (gridCarbonIntensity / baselineGridIntensity) : 1;

  // === Light Duty Vehicle Calculations ===
  // Calculate parking pricing VMT reduction first
  const parkingVMTReduction = calculateParkingPricingVMTReduction(
    scenario.parkingPricing,
    baseline.vmt
  );
  
  // Total VMT reduction: use net statewide savings for Compact Development when provided (includes out-of-boundary)
  const compactLdvMT = options?.compactDevelopmentLdvReductionMT ?? 0;
  const otherVmtPercent = Math.max(0,
    scenario.vmtReductionPercent - (scenario.vmtReductionComponents?.transitOrientedDevelopment ?? 0) + parkingVMTReduction
  );
  const effectiveCompactPercent = baseline.lightDutyGHG > 0 && compactLdvMT > 0
    ? Math.min(100, (compactLdvMT / baseline.lightDutyGHG) * 100)
    : 0;
  // Homes in or near downtown: 29% fewer VMT than average for that share of homes
  const downtownHomesPercent = Math.max(0, Math.min(100, scenario.downtownHomesPercent ?? 70));
  const downtownVMTReductionPercent = 0.29 * (downtownHomesPercent / 100);
  const totalVMTReductionPercent = effectiveCompactPercent + otherVmtPercent + downtownVMTReductionPercent;
  const newVMT = baseline.vmt * (1 - totalVMTReductionPercent / 100);
  const newEVPercent = Math.min(scenario.evAdoptionPercent, 100);
  const icePercent = 100 - newEVPercent;
  
  // Calculate new fleet-weighted GHG per mile using EMFAC data (same as GHG Inventory)
  const baselineGhgPerMile = baseline.lightDutyGHG / baseline.vmt;
  
  // Get EMFAC data for accurate emissions per mile (same as GHG Inventory)
  report(2, 'Loading vehicle emission factors (EMFAC)…');
  const emfacData = await fetchEMFACDataForPolicy(location, emfacVersion);
  
  // Prefer the 2023 EMFAC factor to match baseline; fall back to average
  const validEmissionFactors = emfacData.filter(d => d.ghg_per_mile > 0);
  const ef2023 = validEmissionFactors.find(d => d.year === 2023)?.ghg_per_mile;
  const avgEmissionFactor = validEmissionFactors.length > 0
    ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
    : 0.0008; // Fallback (tons CO2 per mile)

  // Align calculation baseline with inventory by using baselineGhgPerMile when available
  const emfacGhgPerMile = ef2023 || baselineGhgPerMile || avgEmissionFactor;
  console.log('Policy Impact: Using EMFAC factor:', emfacGhgPerMile, '(2023 preferred, else baseline/avg)');

  report(3, 'Computing reductions by sector and policy contributions…');
  
  // Use EMFAC-based emissions per mile for ICE vehicles
  const iceGhgPerMile = emfacGhgPerMile * CONSTANTS.ICE_IMPROVEMENT_FACTOR;
  // gridCarbonIntensity is in kg CO2/kWh, EV_KWH_PER_MILE is kWh/mile
  // Result is kg CO2/mile, convert to tons CO2/mile by dividing by 1000
  const evGhgPerMile = (gridCarbonIntensity * CONSTANTS.EV_KWH_PER_MILE) / 1000;

  const newFleetGhgPerMile = (icePercent / 100 * iceGhgPerMile) + (newEVPercent / 100 * evGhgPerMile);
  const newLightDutyGHG = newVMT * newFleetGhgPerMile;

  // === Heavy Duty Vehicle Calculations ===
  const hdvElectricPercent = scenario.hdvElectrificationPercent;
  const hdvICEPercent = 100 - hdvElectricPercent;

  const hdvElectricGHG = (hdvElectricPercent / 100) * baseline.heavyDutyGHG *
    CONSTANTS.HDV_ELECTRIFICATION_EFFICIENCY * gridDecarbonizationFactor;
  const hdvICEGHG = (hdvICEPercent / 100) * baseline.heavyDutyGHG;
  const newHeavyDutyGHG = hdvElectricGHG + hdvICEGHG;

  // === Buildings: BAU 2045 → efficiency layer → additional electrification → RNG ===
  const bauTherms = baseline.bauTherms2045 ?? baseline.naturalGasTherms;
  const bauKwh = baseline.bauKwh2045 ?? baseline.electricityKwh;
  const rngShare = options?.buildingsRngPercentOverride != null
    ? options.buildingsRngPercentOverride / 100
    : (baseline.rngShare2045 ?? 0);

  const rawEfficiency: EnergyEfficiencyComponents =
    scenario.energyEfficiencyComponents && hasAnyEfficiencyComponent(scenario.energyEfficiencyComponents)
      ? scenario.energyEfficiencyComponents
      : distributeEnergyEfficiencyTarget(
          Math.max(0, Math.min(40, scenario.energyEfficiencyTargetPercent ?? scenario.energyEfficiencyPercent ?? 0)),
          scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()
        );
  const effectiveComponents = normalizeEnergyEfficiencyComponents(rawEfficiency);

  const efficiency2045 = applyEfficiencyForYear(bauTherms, bauKwh, 2045, effectiveComponents, options?.efficiencyLayerOptions);
  const thermsAfterEff = efficiency2045.thermsAfter;
  const kwhAfterEff = efficiency2045.kwhAfter;

  const bldgElecPct = Math.max(0, Math.min(100, scenario.heatPumpAdoptionPercent || 0));
  const elecFrac = bldgElecPct / 100;
  const thermsReplaced = thermsAfterEff * elecFrac;
  const thermsRemaining = Math.max(0, thermsAfterEff - thermsReplaced);
  const cop = Math.max(2.5, Math.min(4, options?.buildingsCopOverride ?? 3.5));
  const addedKwh = thermsReplaced * (KWH_PER_THERM / cop);
  const totalKwh = kwhAfterEff + addedKwh;

  const newNaturalGasGHG = (thermsRemaining * (1 - rngShare) * 5.29) / 1000;

  // Electricity emissions from kWh * g/kWh; apply carbon-intensity factor after efficiency.
  const gPerKwh = gPerKwh2045;
  const newElectricityGHG = (totalKwh * gPerKwh) / 1_000_000;

  // Apply additional organics diversion beyond BAU to waste emissions (0–50% by 2045).
  // NOTE: This is a simple proportional approach (direct emissions only), not a landfill gas/end-use lifecycle model.
  const organicsDiversionPercent = Math.max(0, Math.min(50, (scenario as any).organicsDiversionPercent || 0));
  const organicsDiversionFrac = organicsDiversionPercent / 100;
  const newWasteGHG = baseline.wasteGHG * (1 - organicsDiversionFrac);
  const newAgricultureGHG = baseline.agricultureGHG;

  // === Total Projected GHG ===
  const totalProjectedGHG =
    newLightDutyGHG +
    newHeavyDutyGHG +
    newNaturalGasGHG +
    newElectricityGHG +
    newWasteGHG +
    newAgricultureGHG;

  // === Calculate reductions ===
  const lightDutyReduction = baseline.lightDutyGHG - newLightDutyGHG;
  const heavyDutyReduction = baseline.heavyDutyGHG - newHeavyDutyGHG;
  const naturalGasReduction = baseline.naturalGasGHG - newNaturalGasGHG;
  const electricityReduction = baseline.electricityGHG - newElectricityGHG;
  const wasteReduction = baseline.wasteGHG - newWasteGHG;
  const totalReduction = baseline.totalGHG - totalProjectedGHG;
  const percentReduction = (totalReduction / baseline.totalGHG) * 100;

  // === Calculate individual policy contributions (approximate) ===
  // These are calculated by running each policy in isolation

  // EV adoption impact (isolated)
  const evOnlyVMT = baseline.vmt;
  const evOnlyFleetGhgPerMile = ((100 - scenario.evAdoptionPercent) / 100 * emfacGhgPerMile) +
    (scenario.evAdoptionPercent / 100 * evGhgPerMile);
  const evOnlyLDGHG = evOnlyVMT * evOnlyFleetGhgPerMile;
  // Ensure zero adoption yields zero contribution; clamp small negatives due to factor mismatches
  const rawEvContribution = baseline.lightDutyGHG - evOnlyLDGHG;
  const evContribution = scenario.evAdoptionPercent === 0 ? 0 : Math.max(0, rawEvContribution);

  // VMT reduction impact (includes both traditional VMT reduction and parking pricing)
  const totalVMTReduction = scenario.vmtReductionPercent + parkingVMTReduction;
  const vmtOnlyReduction = baseline.lightDutyGHG * (totalVMTReduction / 100);

  // VMT reduction by component.
  // Compact: prefer net statewide MT from the compact model; else scenario.vmtReductionComponents.transitOrientedDevelopment as incremental compact VMT % of baseline LDV (not housing growth %).
  const compactComponentLdvMT = compactLdvMT > 0
    ? compactLdvMT
    : baseline.lightDutyGHG *
      (Math.max(0, Math.min(100, scenario.vmtReductionComponents?.transitOrientedDevelopment ?? 0)) / 100);
  const downtownHomesContribution = baseline.lightDutyGHG * (downtownVMTReductionPercent / 100);
  const vmtReductionByComponent = {
    transitOrientedDevelopment: compactComponentLdvMT,
    activeTransportation: baseline.lightDutyGHG * (scenario.vmtReductionComponents.activeTransportation / 100),
    transitImprovements: baseline.lightDutyGHG * (scenario.vmtReductionComponents.transitImprovements / 100),
  };

  // Parking pricing VMT reduction contribution
  const parkingPricingVMTReduction = calculateParkingPricingVMTReduction(
    scenario.parkingPricing,
    baseline.vmt
  );
  const parkingPricingContribution = baseline.lightDutyGHG * (parkingPricingVMTReduction / 100);

  const bauNgTons = (bauTherms * (1 - rngShare) * 5.29) / 1000;
  const bauElecTons = (bauKwh * gPerKwh2045) / 1_000_000;
  const effNgTons = (thermsAfterEff * (1 - rngShare) * 5.29) / 1000;
  const effElecTons = (kwhAfterEff * gPerKwh2045) / 1_000_000;
  const energyEfficiencyContribution =
    Math.max(0, (bauNgTons - effNgTons) + (bauElecTons - effElecTons));

  const elecThermsReplaced = thermsAfterEff * elecFrac;
  const elecThermsRemaining = Math.max(0, thermsAfterEff - elecThermsReplaced);
  const elecAddedKwh = elecThermsReplaced * (KWH_PER_THERM / cop);
  const elecTotalKwh = kwhAfterEff + elecAddedKwh;
  const elecNgTons = (elecThermsRemaining * (1 - rngShare) * 5.29) / 1000;
  const elecElecTons = (elecTotalKwh * gPerKwh2045) / 1_000_000;
  const heatPumpContribution = Math.max(0, effNgTons + effElecTons - (elecNgTons + elecElecTons));

  // Solar capacity impact (on EVs and heat pumps)
  const solarGridImpact = (baselineGridIntensity - gridCarbonIntensity) / baselineGridIntensity;
  const solarContribution = (newEVPercent / 100 * newVMT * CONSTANTS.EV_KWH_PER_MILE * solarGridImpact) / 1000 +
    (newElectricityGHG * solarGridImpact);

  // HDV electrification impact
  const hdvContribution = baseline.heavyDutyGHG - newHeavyDutyGHG;

  // Organics diversion impact
  const organicsDiversionContribution = baseline.wasteGHG - newWasteGHG;

  // Micromobility impact (if configured)
  let micromobilityContribution = 0;
  if (scenario.micromobility && (scenario.micromobility.scalePercent || 0) > 0) {
    micromobilityContribution = await calculateMicromobilityReduction(
      location,
      scenario.micromobility,
      emfacGhgPerMile,
      gridCarbonIntensity
    );
  }

  return {
    baseline,
    projected: {
      lightDutyGHG: newLightDutyGHG,
      heavyDutyGHG: newHeavyDutyGHG,
      naturalGasGHG: newNaturalGasGHG,
      electricityGHG: newElectricityGHG,
      wasteGHG: newWasteGHG,
      agricultureGHG: newAgricultureGHG,
      totalGHG: totalProjectedGHG,
    },
    reductions: {
      lightDutyReduction,
      heavyDutyReduction,
      naturalGasReduction,
      electricityReduction,
      wasteReduction,
      totalReduction,
      percentReduction,
    },
    contributionsByPolicy: {
      evAdoption: evContribution,
      vmtReduction: vmtOnlyReduction,
      vmtReductionByComponent,
      downtownHomes: downtownHomesContribution,
      parkingPricing: parkingPricingContribution,
      micromobility: micromobilityContribution,
      energyEfficiency: energyEfficiencyContribution,
      heatPumpAdoption: heatPumpContribution,
      solarCapacity: solarContribution,
      hdvElectrification: hdvContribution,
      organicsDiversion: organicsDiversionContribution,
    },
  };
}

/**
 * Get default policy scenario (baseline)
 */
export function getDefaultScenario(): PolicyScenario {
  return {
    evAdoptionPercent: 0,
    vmtReductionPercent: 0,
    vmtReductionComponents: getDefaultVmtComponents(),
    parkingPricing: {
      priceIncreasePercent: 0,
      vmtAffectedPercent: 0,
      startingPricePerHour: 0,
      commuteByCarPercent: 60, // Will be fetched from database
    },
    micromobility: {
      yearImplemented: 2025,
      tripsPerResident: 3,
      percentBike: 60,
      percentScooter: 40,
      milesPerBikeTrip: 1.61,
      milesPerScooterTrip: 1.2,
      percentBikeTripsDisplaceCars: 30,
      percentScooterTripsDisplaceCars: 20,
      kwhPerMileBike: 0.02,
      kwhPerMileScooter: 0.04,
      scalePercent: 0,
    },
    downtownHomesPercent: 70,
    energyEfficiencyTargetPercent: 0,
    energyEfficiencyComponents: getDefaultEnergyEfficiencyComponents(),
    energyEfficiencyPercent: 0,
    heatPumpAdoptionPercent: 0,
    gridCleanElectricityYear: 2045,
    solarCapacityMultiplier: 1.0,
    hdvElectrificationPercent: 0,
    organicsDiversionPercent: 0,
    buildingEndUseAssumptions: getDefaultBuildingEndUseAssumptions(),
    applyBauExistingStockEfficiency: true,
  };
}

/**
 * Get predefined scenario examples
 */
export function getPredefinedScenarios(): { [key: string]: PolicyScenario } {
  return {
    baseline: {
      evAdoptionPercent: 0,
      vmtReductionPercent: 0,
      vmtReductionComponents: {
        transitOrientedDevelopment: 0,
        activeTransportation: 0,
        transitImprovements: 0,
      },
      parkingPricing: {
        priceIncreasePercent: 0,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      downtownHomesPercent: 70,
      energyEfficiencyTargetPercent: 0,
      energyEfficiencyComponents: getDefaultEnergyEfficiencyComponents(),
      energyEfficiencyPercent: 0,
      heatPumpAdoptionPercent: 0,
      gridCleanElectricityYear: 2045,
      solarCapacityMultiplier: 1.0,
      hdvElectrificationPercent: 0,
      organicsDiversionPercent: 0, // No additional local diversion beyond BAU
      buildingEndUseAssumptions: getDefaultBuildingEndUseAssumptions(),
      applyBauExistingStockEfficiency: true,
    },
    fullImplementation: {
      evAdoptionPercent: 100,
      vmtReductionPercent: 14, // Sum of components (compact 0–2% scale)
      vmtReductionComponents: {
        transitOrientedDevelopment: 2,
        activeTransportation: 4,
        transitImprovements: 8,
      },
      parkingPricing: {
        priceIncreasePercent: 300, // Maximum
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      downtownHomesPercent: 70,
      energyEfficiencyTargetPercent: 40,
      energyEfficiencyComponents: {
        residentialRetrofitExistingPercent: 20,
        residentialReachCodePercent: 8,
        commercialRetrofitExistingPercent: 6,
        otherEfficiencyPercent: 6,
      },
      energyEfficiencyPercent: 40,
      heatPumpAdoptionPercent: 100,
      gridCleanElectricityYear: 2025, // Earliest (all the way right)
      solarCapacityMultiplier: 5.0,
      hdvElectrificationPercent: 100,
      organicsDiversionPercent: 40, // Placeholder: strong additional diversion beyond BAU
      buildingEndUseAssumptions: getDefaultBuildingEndUseAssumptions(),
    },
    aggressive: {
      evAdoptionPercent: 80,
      vmtReductionPercent: 7,
      vmtReductionComponents: {
        transitOrientedDevelopment: 1,
        activeTransportation: 2,
        transitImprovements: 4,
      },
      parkingPricing: {
        priceIncreasePercent: 100,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      downtownHomesPercent: 70,
      energyEfficiencyTargetPercent: 20,
      energyEfficiencyComponents: {
        residentialRetrofitExistingPercent: 10,
        residentialReachCodePercent: 4,
        commercialRetrofitExistingPercent: 3,
        otherEfficiencyPercent: 3,
      },
      energyEfficiencyPercent: 20,
      heatPumpAdoptionPercent: 70,
      gridCleanElectricityYear: 2025,
      solarCapacityMultiplier: 3.0,
      hdvElectrificationPercent: 50,
      organicsDiversionPercent: 25, // Placeholder: medium-high additional diversion beyond BAU
      buildingEndUseAssumptions: getDefaultBuildingEndUseAssumptions(),
    },
    moderate: {
      evAdoptionPercent: 50,
      vmtReductionPercent: 4,
      vmtReductionComponents: {
        transitOrientedDevelopment: 1,
        activeTransportation: 1,
        transitImprovements: 2,
      },
      parkingPricing: {
        priceIncreasePercent: 50,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      downtownHomesPercent: 70,
      energyEfficiencyTargetPercent: 10,
      energyEfficiencyComponents: {
        residentialRetrofitExistingPercent: 5,
        residentialReachCodePercent: 2,
        commercialRetrofitExistingPercent: 1.5,
        otherEfficiencyPercent: 1.5,
      },
      energyEfficiencyPercent: 10,
      heatPumpAdoptionPercent: 40,
      gridCleanElectricityYear: 2030,
      solarCapacityMultiplier: 2.0,
      hdvElectrificationPercent: 25,
      organicsDiversionPercent: 10, // Placeholder: low additional diversion beyond BAU
      buildingEndUseAssumptions: getDefaultBuildingEndUseAssumptions(),
    },
    electrificationFocus: {
      evAdoptionPercent: 90,
      vmtReductionPercent: 4,
      vmtReductionComponents: {
        transitOrientedDevelopment: 0,
        activeTransportation: 0,
        transitImprovements: 0,
      },
      parkingPricing: {
        priceIncreasePercent: 0,
        vmtAffectedPercent: 15,
        startingPricePerHour: 0,
        commuteByCarPercent: 60,
      },
      downtownHomesPercent: 70,
      energyEfficiencyTargetPercent: 5,
      energyEfficiencyComponents: distributeEnergyEfficiencyTarget(5, getDefaultEnergyEfficiencyComponents()),
      energyEfficiencyPercent: 5,
      heatPumpAdoptionPercent: 65,
      gridCleanElectricityYear: 2045,
      solarCapacityMultiplier: 1.0,
      hdvElectrificationPercent: 0,
      organicsDiversionPercent: 0,
      buildingEndUseAssumptions: getDefaultBuildingEndUseAssumptions(),
    },
  };
}
